import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { queryClient } from '../components/queryClient';
import { useFleet } from '../hooks/useFleet';
import { useHasPermission } from '../hooks/useHasPermission';
import { ChangeRequestStatus, ConfigType, ConfigurationManagementService, EntityType } from '../services/configurationManagement.service';
import { useFeatureFlags } from './featureFlags';

const QUERY_KEY_BASE = 'changeRequests';

/**
 * Hook to fetch a single change request by ID
 */
export const useChangeRequestById = (id: number) => {
  const hasPermission = useHasPermission('configurations:read:change_requests');
  const { ccr_enabled } = useFeatureFlags();

  return useQuery({
    queryKey: [QUERY_KEY_BASE, 'byId', id],
    queryFn: () => ConfigurationManagementService.getById(id),
    enabled: ccr_enabled === true && !!id && hasPermission,
  });
};

/**
 * Hook to check if self-review is allowed for a change request.
 * Returns true if the current user can approve their own request.
 */
export const useSelfReviewPermission = (id: number | undefined) => {
  const hasPermission = useHasPermission('configurations:write:change_requests');
  const { ccr_enabled } = useFeatureFlags();

  return useQuery({
    queryKey: [QUERY_KEY_BASE, 'selfReviewPermission', id],
    queryFn: () => ConfigurationManagementService.getSelfReviewPermission(id as number),
    enabled: ccr_enabled === true && !!id && hasPermission,
    select: (data) => data.allowed,
  });
};

export interface UseChangeRequestsOptions {
  enabled?: boolean;
  status?: ChangeRequestStatus;
}

/**
 * Hook to fetch change requests for a specific entity.
 * @param entityType - The type of entity (e.g. 'fleet', 'authority')
 * @param entityId - The ID of the entity
 * @param options - Options object with enabled and status filter
 */
export const useChangeRequests = (entityType: EntityType, entityId: string, options?: UseChangeRequestsOptions) => {
  const hasPermission = useHasPermission('configurations:read:change_requests');
  const { ccr_enabled } = useFeatureFlags();

  const { enabled = true, status } = options ?? {};

  const queryKey = [QUERY_KEY_BASE, entityType, entityId, status];

  return useQuery({
    queryKey,
    queryFn: () => ConfigurationManagementService.listChangeRequests(entityType, entityId, status),
    enabled: ccr_enabled === true && !!entityId && enabled && hasPermission,
  });
};

export interface UsePendingChangeRequestsOptions {
  enabled?: boolean;
  configType?: ConfigType;
}

/**
 * Hook to fetch pending change requests for a specific entity.
 * @param entityType - The type of entity (e.g. 'fleet', 'authority')
 * @param entityId - The ID of the entity
 * @param options - Options object with enabled and configType filter
 * @returns Query result with pendingChangeRequests array and hasPendingChangeRequest boolean
 */
export const usePendingChangeRequests = (entityType: EntityType, entityId: string, options?: UsePendingChangeRequestsOptions) => {
  const hasPermission = useHasPermission('configurations:read:change_requests');
  const { ccr_enabled } = useFeatureFlags();

  const { enabled = true, configType } = options ?? {};

  const queryKey = [QUERY_KEY_BASE, entityType, entityId, ChangeRequestStatus.Pending];

  const query = useQuery({
    queryKey,
    queryFn: () => ConfigurationManagementService.listChangeRequests(entityType, entityId, ChangeRequestStatus.Pending),
    enabled: ccr_enabled === true && !!entityId && enabled && hasPermission,
  });

  const pendingChangeRequests = useMemo(() => {
    if (!ccr_enabled || !Array.isArray(query.data)) {
      return [];
    }

    return query.data.filter((cr) => cr.status === ChangeRequestStatus.Pending && (!configType || cr.config_type === configType));
  }, [query.data, configType, ccr_enabled]);

  return {
    ...query,
    pendingChangeRequests,
    hasPendingChangeRequest: pendingChangeRequests.length > 0,
    // Override isLoading when CCR is disabled - no point showing loading state
    isLoading: ccr_enabled === true && query.isLoading,
  };
};

/**
 * Hook to fetch change requests for the current fleet (from useFleet context).
 */
export const useCurrentFleetChangeRequests = (options?: UseChangeRequestsOptions) => {
  const fleet = useFleet();

  return useChangeRequests('fleet', fleet.uuid, options);
};

/**
 * Hook to fetch pending change requests for the current fleet (from useFleet context).
 */
export const useCurrentFleetPendingChangeRequests = (options?: UsePendingChangeRequestsOptions) => {
  const fleet = useFleet();

  return usePendingChangeRequests('fleet', fleet.uuid, options);
};

export const forceRefreshChangeRequests = () => {
  queryClient.invalidateQueries({ queryKey: [QUERY_KEY_BASE] });
};
